/** 課金ダッシュボードの集計期間プリセット。 */
import type {
	BillingDailyCredits,
	BillingEventCredits,
	BillingRate,
	BillingUsageOrigin,
	DeviceLicense,
	DeviceLicenseEvent,
	DeviceLicenseEventKind,
} from "@/lib/api";
import { sum } from "@/lib/sum";
export type BillingPeriod =
	| "allTime"
	| "pastYear"
	| "pastMonth"
	| "pastWeek"
	| "yearToDate"
	| "monthToDate"
	| "weekToDate";

export const BILLING_PERIODS: readonly { id: BillingPeriod; label: string }[] = [
	{ id: "allTime", label: "全期間" },
	{ id: "pastYear", label: "過去1年" },
	{ id: "pastMonth", label: "過去1ヶ月" },
	{ id: "pastWeek", label: "過去1週間" },
	{ id: "yearToDate", label: "年初来" },
	{ id: "monthToDate", label: "月初来" },
	{ id: "weekToDate", label: "週初来" },
];

/** JST (UTC+9) の固定オフセット。日本に夏時間はないため定数でよい。 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/**
 * 期間プリセットの開始日時を返す (全期間は null)。終端は常に「現在」。
 * 課金期間の境界は閲覧者のブラウザ設定に依存させず、JST の壁時計で求める。
 * 実装は now を +9 時間ずらした Date に UTC 系メソッドを適用して JST の年月日を取り、
 * 結果を -9 時間戻して UTC 瞬間に変換する。
 * 過去 1 ヶ月は月末起点だと数日ずれることがある (例: 5/31 → 5/1) が、プリセットの
 * 用途では許容する。
 */
export function billingPeriodStart(period: BillingPeriod, now: Date): Date | null {
	const jst = new Date(now.getTime() + JST_OFFSET_MS);
	switch (period) {
		case "allTime":
			return null;
		case "pastYear": {
			const start = new Date(jst);
			start.setUTCFullYear(start.getUTCFullYear() - 1);
			return new Date(start.getTime() - JST_OFFSET_MS);
		}
		case "pastMonth": {
			const start = new Date(jst);
			start.setUTCMonth(start.getUTCMonth() - 1);
			return new Date(start.getTime() - JST_OFFSET_MS);
		}
		case "pastWeek": {
			const start = new Date(jst);
			start.setUTCDate(start.getUTCDate() - 7);
			return new Date(start.getTime() - JST_OFFSET_MS);
		}
		case "yearToDate":
			return new Date(Date.UTC(jst.getUTCFullYear(), 0, 1) - JST_OFFSET_MS);
		case "monthToDate":
			return new Date(Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), 1) - JST_OFFSET_MS);
		case "weekToDate": {
			// 週の始まりは月曜 (ISO 8601)。getUTCDay は 0 = 日曜なので月曜からの経過日数へ直す。
			const daysSinceMonday = (jst.getUTCDay() + 6) % 7;
			return new Date(
				Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate() - daysSinceMonday) -
					JST_OFFSET_MS,
			);
		}
	}
}

/** クレジット付与の未使用分が失効するまでの日数。バックエンドの CreditExpiry.ExpiryDays と揃える。 */
export const CREDIT_EXPIRY_DAYS = 150;

/**
 * 顧客向けの課金カテゴリ。内部のメータイベント種別 (BillingEventType のドット区切り
 * 識別子) を顧客に見せる区分へまとめ、ラベルは日本語で表示する。API へは eventTypes に
 * 展開して渡す。
 */
export interface BillingCategory {
	id: string;
	label: string;
	eventTypes: readonly string[];
	/** 使用量グラフの系列色。期間や絞り込みで並びが変わっても、カテゴリの色は変えない。 */
	color: string;
}

export const BILLING_CATEGORIES: readonly BillingCategory[] = [
	{
		id: "modelApi",
		label: "LLM利用料",
		eventTypes: [
			"model.input_tokens",
			"model.cache_write_tokens",
			"model.cache_read_tokens",
			"model.output_tokens",
		],
		color: "var(--chart-model-api)",
	},
	{
		id: "agentRuntime",
		label: "エージェント実行",
		eventTypes: ["agent.active_seconds"],
		color: "var(--chart-agent-runtime)",
	},
	{
		id: "environmentServe",
		label: "サーバー基本料金",
		eventTypes: ["environment.uptime_seconds", "environment.stopped_seconds"],
		color: "var(--chart-environment-serve)",
	},
	{
		id: "cpu",
		label: "エージェントコンピューティング",
		eventTypes: ["cpu.vcpu_seconds", "memory.gib_seconds"],
		color: "var(--chart-cpu)",
	},
	{
		id: "gpu",
		label: "実験コンピューティング (GPU)",
		eventTypes: ["gpu.seconds"],
		color: "var(--chart-gpu)",
	},
	{
		id: "storage",
		label: "永続ストレージ",
		eventTypes: ["storage.gib_hours", "storage.io_read_gb", "storage.io_write_gb"],
		color: "var(--chart-storage)",
	},
	{
		id: "network",
		label: "データ転送 (送信)",
		eventTypes: ["network.egress_gb"],
		color: "var(--chart-network)",
	},
	{
		id: "externalTools",
		label: "外部サービス",
		eventTypes: ["external_tool.calls"],
		color: "var(--chart-external-tools)",
	},
	{
		id: "webSearch",
		label: "Web 検索",
		eventTypes: ["web_search.requests"],
		color: "var(--chart-web-search)",
	},
	{
		id: "deviceLicense",
		label: "デバイスのライセンス",
		eventTypes: ["device.license_months"],
		color: "var(--chart-device-license)",
	},
];

/** 選択中のカテゴリ ID をバックエンドへ渡すイベント種別識別子へ展開する。 */
export function categoryIdsToEventTypes(categoryIds: readonly string[]): string[] {
	return BILLING_CATEGORIES.filter((c) => categoryIds.includes(c.id)).flatMap((c) => [
		...c.eventTypes,
	]);
}

/** イベント種別識別子 → カテゴリ ID の逆引き。 */
const EVENT_TYPE_TO_CATEGORY_ID: Readonly<Record<string, string>> = Object.fromEntries(
	BILLING_CATEGORIES.flatMap((c) => c.eventTypes.map((t) => [t, c.id])),
);

/** UTC 瞬間を JST の暦日 (yyyy-MM-dd) にする。 */
export function jstDateKey(instant: Date): string {
	return new Date(instant.getTime() + JST_OFFSET_MS).toISOString().slice(0, 10);
}

/** JST の暦日 (yyyy-MM-dd) に日数を足す。 */
function addDaysToDateKey(dateKey: string, days: number): string {
	const base = new Date(`${dateKey}T00:00:00Z`);
	base.setUTCDate(base.getUTCDate() + days);
	return base.toISOString().slice(0, 10);
}

/** 2 つの暦日 (yyyy-MM-dd) の差の日数 (b - a)。 */
function diffDays(a: string, b: string): number {
	return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** 使用量グラフのバケット 1 件。date はバケット先頭の JST 暦日。 */
export interface UsageChartBucket {
	date: string;
	/** バケットに含まれる日数 (末尾の週バケットは 7 日に満たないことがある)。 */
	days: number;
	/** カテゴリ ID → クレジット。消費のないカテゴリはキーごと持たない。 */
	byCategory: Record<string, number>;
	total: number;
}

/** 使用量グラフのカテゴリ 1 件と、その期間全体の消費クレジット。 */
export interface UsageChartCategory {
	category: BillingCategory;
	credits: number;
}

/** 使用量グラフで料金表のタブ 1 つにまとめたカテゴリ。グラフの色相はタブごとに分かれる。 */
export interface UsageChartTab {
	tab: BillingRateTab;
	credits: number;
	/** 消費のあるカテゴリ (期間の消費の降順)。 */
	categories: UsageChartCategory[];
}

/** 使用量グラフの描画データ。 */
export interface UsageChartData {
	/** 期間が 1 ヶ月以下なら日別の縦棒、超えるなら面グラフ。 */
	kind: "column" | "area";
	/** 1 バケットの日数。長期間はグラフが点の羅列にならないよう週 (7 日) で丸める。 */
	bucketDays: 1 | 7;
	buckets: UsageChartBucket[];
	/** 消費のあるタブ (期間の消費の降順)。 */
	tabs: UsageChartTab[];
	/** tabs のカテゴリを順に並べたもの。積み上げ (下から)・カテゴリ別の一覧の並び順に使う (ツールチップは逆順)。 */
	categories: UsageChartCategory[];
}

/**
 * この日数までは日別の積み上げ縦棒で描く。「過去 1 ヶ月」は端の日を両方含むと
 * 最大 32 日になるため、1 ヶ月ちょうどが面グラフへ倒れないよう 32 とする。
 */
const COLUMN_CHART_MAX_DAYS = 32;

/** この日数を超えたら週単位のバケットへ丸める (約 3 ヶ月)。 */
const WEEKLY_BUCKET_MIN_DAYS = 93;

/**
 * 日次集計をグラフ描画用のバケット列へ変換する。期間の全日を欠けなく並べ、消費の
 * ない日は 0 で埋める。from が null (全期間) なら最初の消費日を先頭にする。
 * 期間はグラフの形も決める: 1 ヶ月以下は日別の縦棒、それより長ければ面グラフ、
 * 3 ヶ月を超えたら点が密になりすぎないよう週単位に丸める (末尾の週は部分週に
 * なることがあり、days にその日数を持つ)。
 */
export function buildUsageChartData(
	rows: readonly BillingDailyCredits[],
	from: Date | null,
	now: Date,
): UsageChartData {
	const endDay = jstDateKey(now);
	const startDay = from !== null ? jstDateKey(from) : (rows[0]?.date ?? endDay);
	const dayCount = Math.max(diffDays(startDay, endDay), 0) + 1;

	const kind = dayCount > COLUMN_CHART_MAX_DAYS ? "area" : "column";
	const bucketDays = dayCount >= WEEKLY_BUCKET_MIN_DAYS ? 7 : 1;
	const bucketCount = Math.ceil(dayCount / bucketDays);

	const buckets: UsageChartBucket[] = Array.from({ length: bucketCount }, (_, i) => ({
		date: addDaysToDateKey(startDay, i * bucketDays),
		days: Math.min(bucketDays, dayCount - i * bucketDays),
		byCategory: {},
		total: 0,
	}));

	const categoryTotals = new Map<string, number>();
	for (const row of rows) {
		const categoryId = EVENT_TYPE_TO_CATEGORY_ID[row.eventType];
		if (categoryId === undefined) continue;
		const index = Math.floor(diffDays(startDay, row.date) / bucketDays);
		// 集計と描画の間に日をまたいだ場合など、期間外の行は無視する。
		if (index < 0 || index >= bucketCount) continue;
		const bucket = buckets[index];
		bucket.byCategory[categoryId] = (bucket.byCategory[categoryId] ?? 0) + row.credits;
		bucket.total += row.credits;
		categoryTotals.set(categoryId, (categoryTotals.get(categoryId) ?? 0) + row.credits);
	}

	const tabs = BILLING_RATE_TABS.map((tab) => {
		const categories = BILLING_CATEGORIES.filter((c) => tab.categoryIds.includes(c.id))
			.map((category) => ({ category, credits: categoryTotals.get(category.id) ?? 0 }))
			.filter((c) => c.credits > 0)
			.sort((a, b) => b.credits - a.credits);
		return { tab, credits: sum(categories, (c) => c.credits), categories };
	})
		.filter((t) => t.categories.length > 0)
		.sort((a, b) => b.credits - a.credits);

	return {
		kind,
		bucketDays,
		buckets,
		tabs,
		categories: tabs.flatMap((t) => t.categories),
	};
}

/** イベント種別識別子の表示ラベル。カテゴリ内訳バーのセグメントのツールチップで使う。 */
export const BILLING_EVENT_TYPE_LABELS: Readonly<Record<string, string>> = {
	"environment.uptime_seconds": "サーバー基本料金 (稼働中)",
	"environment.stopped_seconds": "サーバー基本料金 (停止中)",
	"agent.active_seconds": "エージェント実行",
	"model.input_tokens": "モデル入力トークン",
	"model.cache_write_tokens": "キャッシュ書込",
	"model.cache_read_tokens": "キャッシュ読出",
	"model.output_tokens": "モデル出力トークン",
	"cpu.vcpu_seconds": "CPU (vCPU)",
	"memory.gib_seconds": "メモリ",
	"gpu.seconds": "GPU",
	"storage.gib_hours": "ストレージ",
	"storage.io_read_gb": "ストレージ読み出し",
	"storage.io_write_gb": "ストレージ書き込み",
	"network.egress_gb": "ネットワーク送信",
	"external_tool.calls": "外部ツール呼び出し",
	"web_search.requests": "Web 検索",
	"device.license_months": "デバイスのライセンス",
};

/** 内訳バーのセグメント 1 件。key は同一カテゴリ内で一意。 */
export interface BillingSegment {
	key: string;
	label: string;
	credits: number;
}

/** カテゴリ 1 件分の消費内訳。 */
export interface BillingCategoryBreakdown {
	category: BillingCategory;
	credits: number;
	segments: BillingSegment[];
}

/** 環境 1 件分の消費内訳。プロジェクトカードはこの単位でカテゴリ別バーを並べる。 */
export interface BillingEnvironmentBreakdown {
	environmentId: string | null;
	label: string;
	credits: number;
	categories: BillingCategoryBreakdown[];
}

/** プロジェクトに紐付かない消費の、出どころごとの見出し。"none" は出どころを記録する前の消費。 */
export const BILLING_ORIGIN_NAMES: Readonly<Record<BillingUsageOrigin | "none", string>> = {
	helpAgent: "ヘルプエージェント",
	deviceLicense: "デバイスのライセンス",
	none: "プロジェクトに紐付かない消費",
};

/** 環境に紐付かない消費をまとめるグループのキー。 */
const NO_ENVIRONMENT_KEY = "none";

/** 環境グループの表示名。環境に紐付かない消費と削除済み環境を区別する。 */
function environmentLabel(entry: BillingEventCredits): string {
	if (entry.environmentName) return entry.environmentName;
	return entry.environmentId === null ? "エージェントに紐付かない消費" : "(削除されたエージェント)";
}

/**
 * バックエンドの内訳を表示用のカテゴリ別内訳へ変換する。消費のないカテゴリ・
 * セグメントは除き、カテゴリもカテゴリ内のセグメントも消費クレジットの降順に並べる。
 */
export function categorizeEventCredits(
	byEvent: readonly BillingEventCredits[],
): BillingCategoryBreakdown[] {
	return BILLING_CATEGORIES.map((category) => {
		const segments = new Map<string, BillingSegment>();
		for (const entry of byEvent) {
			if (!category.eventTypes.includes(entry.eventType)) continue;
			const key = entry.eventType;
			const existing = segments.get(key);
			if (existing) existing.credits += entry.credits;
			else
				segments.set(key, {
					key,
					label: BILLING_EVENT_TYPE_LABELS[key] ?? key,
					credits: entry.credits,
				});
		}
		const sorted = [...segments.values()]
			.filter((s) => s.credits > 0)
			.sort((a, b) => b.credits - a.credits);
		return {
			category,
			credits: sum(sorted, (s) => s.credits),
			segments: sorted,
		};
	})
		.filter((b) => b.credits > 0)
		.sort((a, b) => b.credits - a.credits);
}

/**
 * バックエンドの内訳を環境ごとにまとめ、環境の中をさらにカテゴリ別内訳へ変換する。
 * 消費のない環境は除き、消費クレジットの降順に並べる。ただし環境に紐付かない消費は
 * 受け皿のグループなので、額によらず末尾に置く。
 */
export function groupCreditsByEnvironment(
	byEvent: readonly BillingEventCredits[],
): BillingEnvironmentBreakdown[] {
	const groups = new Map<string, { label: string; entries: BillingEventCredits[] }>();
	for (const entry of byEvent) {
		const key = entry.environmentId ?? NO_ENVIRONMENT_KEY;
		const group = groups.get(key);
		if (group) group.entries.push(entry);
		else groups.set(key, { label: environmentLabel(entry), entries: [entry] });
	}

	return [...groups.entries()]
		.map(([key, group]) => {
			const categories = categorizeEventCredits(group.entries);
			return {
				environmentId: key === NO_ENVIRONMENT_KEY ? null : key,
				label: group.label,
				credits: sum(categories, (c) => c.credits),
				categories,
			};
		})
		.filter((g) => g.credits > 0)
		.sort((a, b) => {
			if (a.environmentId === null) return 1;
			if (b.environmentId === null) return -1;
			return b.credits - a.credits;
		});
}

/**
 * 単価の表示単位。台帳の単価は「1 トークン」「1 秒」あたりで桁が小さく読めないため、
 * 顧客が使う単位へ換算して見せる (multiplier を掛けて unit あたりの額にする)。
 */
const BILLING_RATE_UNITS: Readonly<Record<string, { multiplier: number; unit: string }>> = {
	"model.input_tokens": { multiplier: 1_000_000, unit: "100万トークン" },
	"model.cache_write_tokens": { multiplier: 1_000_000, unit: "100万トークン" },
	"model.cache_read_tokens": { multiplier: 1_000_000, unit: "100万トークン" },
	"model.output_tokens": { multiplier: 1_000_000, unit: "100万トークン" },
	"agent.active_seconds": { multiplier: 3600, unit: "時間" },
	"environment.uptime_seconds": { multiplier: 3600, unit: "時間" },
	"environment.stopped_seconds": { multiplier: 3600, unit: "時間" },
	"cpu.vcpu_seconds": { multiplier: 3600, unit: "vCPU時間" },
	"memory.gib_seconds": { multiplier: 3600, unit: "GiB時間" },
	"gpu.seconds": { multiplier: 3600, unit: "GPU時間" },
	// 1 GiB 時間の単価は小数点以下 2 桁に丸めると桁が足りないため 100 GiB 単位で出す。
	"storage.gib_hours": { multiplier: 100, unit: "100 GiB時間" },
	"storage.io_read_gb": { multiplier: 1, unit: "GB" },
	"storage.io_write_gb": { multiplier: 1, unit: "GB" },
	"network.egress_gb": { multiplier: 1, unit: "GB" },
	"external_tool.calls": { multiplier: 1, unit: "回" },
	"web_search.requests": { multiplier: 1, unit: "回" },
	"device.license_months": { multiplier: 1, unit: "台・月" },
};

/** 単価 1 件の表示テキスト。クレジット額と単位を分けて返す (単位は小さく添える)。 */
export function formatUnitRate(
	eventType: string,
	unitPriceCredits: number,
): { credits: string; unit: string } {
	const scale = BILLING_RATE_UNITS[eventType] ?? { multiplier: 1, unit: "単位" };
	return {
		credits: formatRateCredits(unitPriceCredits * scale.multiplier),
		unit: `/ ${scale.unit}`,
	};
}

/**
 * 単価表の 1 グループ。同じ内訳 (モデルなど) の単価をまとめる。key は内訳の識別子で
 * グループの同定と React の key に使い、label は表示名 (null なら内訳の見出しを出さない)。
 */
export interface BillingRateGroup<R extends BillingRate = BillingRate> {
	key: string;
	label: string | null;
	rates: R[];
}

/** カテゴリ 1 件分の単価表。 */
export interface BillingRateCategory<R extends BillingRate = BillingRate> {
	category: BillingCategory;
	groups: BillingRateGroup<R>[];
}

/**
 * 単価をダッシュボードと同じカテゴリ区分へまとめ、カテゴリ内はさらに内訳 (モデルなど) で
 * グループ化する。単価が無いカテゴリは除く。並び順はカテゴリ定義のイベント種別順を優先し、
 * グループはバックエンドが返した順 (モデルの設定順) を保つ。
 * グループ化は variantKey で行う。表示名 (variantLabel) は一意性が保証されないため、
 * 表示名で束ねると別々のモデルの単価が 1 グループに混ざって行が消える。
 */
export function groupRatesByCategory<R extends BillingRate>(
	rates: readonly R[],
): BillingRateCategory<R>[] {
	return BILLING_CATEGORIES.map((category) => {
		const groups = new Map<string, BillingRateGroup<R>>();
		for (const rate of rates) {
			if (!category.eventTypes.includes(rate.eventType)) continue;
			const key = rate.variantKey ?? "";
			const group = groups.get(key);
			if (group) group.rates.push(rate);
			else groups.set(key, { key, label: rate.variantLabel, rates: [rate] });
		}
		for (const group of groups.values()) {
			group.rates.sort(
				(a, b) =>
					category.eventTypes.indexOf(a.eventType) - category.eventTypes.indexOf(b.eventType),
			);
		}
		return { category, groups: [...groups.values()] };
	}).filter((c) => c.groups.length > 0);
}

/** 長文入力の単価区分の variantKey 接尾辞。バックエンドの LongContextPricing.Tier に対応する。 */
const LONG_CONTEXT_VARIANT_SUFFIX = ":long";

/** LLM 利用料の単価タブ 1 枚分。 */
export interface BillingRateModelTab {
	key: string;
	label: string;
	groups: BillingRateGroup[];
}

const OTHER_MODELS_TAB_KEY = "__others";

/**
 * LLM 利用料の内訳 (variantKey = モデル ID、長文入力の区分は "{モデル ID}:long") をタブへ
 * まとめる。1 モデル 1 タブで、長文入力の区分は同じモデルのタブに入れる。モデル選択で
 * 「そのほか」に並ぶモデルは、モデル選択と揃えて 1 枚の「そのほか」タブへまとめる。
 * モデル別のタブではモデル名がタブ名と重なるので、通常の区分の内訳名は落とす。
 */
export function groupRatesByModelTab(groups: readonly BillingRateGroup[]): BillingRateModelTab[] {
	const models = new Map<string, BillingRateModelTab & { listedUnderOthers: boolean }>();
	for (const group of groups) {
		const isLong = group.key.endsWith(LONG_CONTEXT_VARIANT_SUFFIX);
		const key = isLong ? group.key.slice(0, -LONG_CONTEXT_VARIANT_SUFFIX.length) : group.key;
		const model = models.get(key) ?? {
			key,
			label: key,
			groups: [],
			listedUnderOthers: group.rates.some((r) => r.listedUnderOthers),
		};
		if (!isLong) model.label = group.label || key;
		model.groups.push(group);
		models.set(key, model);
	}

	const tabs: BillingRateModelTab[] = [];
	const others: BillingRateGroup[] = [];
	for (const model of models.values()) {
		if (model.listedUnderOthers) {
			others.push(...model.groups.map((g) => (g.label ? g : { ...g, label: model.label })));
		} else {
			tabs.push({
				key: model.key,
				label: model.label,
				groups: model.groups.map((g) =>
					g.key.endsWith(LONG_CONTEXT_VARIANT_SUFFIX) ? g : { ...g, label: null },
				),
			});
		}
	}
	if (others.length > 0)
		tabs.push({ key: OTHER_MODELS_TAB_KEY, label: "そのほか", groups: others });
	return tabs;
}

/**
 * 近いカテゴリのまとまり。単価表では単価が数十行になり 1 枚では読めないので、
 * まとまりを 1 タブにして切り替えて見せる (カテゴリ 1 件 = 1 タブではタブが多すぎて
 * 1 行に並ばない)。使用量グラフも同じまとまりで色相と一覧の見出しを分ける。
 */
export interface BillingRateTab {
	id: string;
	label: string;
	categoryIds: readonly string[];
}

export const BILLING_RATE_TABS: readonly BillingRateTab[] = [
	{ id: "model", label: "LLM", categoryIds: ["modelApi"] },
	{
		id: "compute",
		label: "コンピューティング",
		categoryIds: ["agentRuntime", "environmentServe", "cpu", "gpu"],
	},
	{ id: "storage", label: "ストレージ・転送", categoryIds: ["storage", "network"] },
	{ id: "external", label: "外部サービス", categoryIds: ["externalTools", "webSearch"] },
	{ id: "device", label: "デバイス", categoryIds: ["deviceLicense"] },
];

/** タブ 1 枚分の単価表。 */
export interface BillingRateTabContent<R extends BillingRate = BillingRate> {
	tab: BillingRateTab;
	categories: BillingRateCategory<R>[];
}

/**
 * 単価をタブへ振り分ける。単価が 1 件も無いタブは (そのタブを開いても空なので) 落とす。
 * どのタブにも属さないカテゴリの単価は落ちるため、カテゴリを増やしたらタブにも足す。
 */
export function groupRatesByTab<R extends BillingRate>(
	rates: readonly R[],
): BillingRateTabContent<R>[] {
	const byCategoryId = new Map(groupRatesByCategory(rates).map((c) => [c.category.id, c]));
	return BILLING_RATE_TABS.map((tab) => ({
		tab,
		categories: tab.categoryIds
			.map((id) => byCategoryId.get(id))
			.filter((c): c is BillingRateCategory<R> => c !== undefined),
	})).filter((t) => t.categories.length > 0);
}

/**
 * 書き出す CSV のファイル名。ダウンロードフォルダで既存のファイルと衝突しないよう、
 * 閲覧者のローカル時刻で日時を付ける。
 */
export function billingCsvFilename(now: Date, prefix = "billing-events"): string {
	const pad = (value: number, length = 2) => value.toString().padStart(length, "0");
	const date = `${pad(now.getFullYear(), 4)}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
	const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
	return `${prefix}-${date}-${time}.csv`;
}

/**
 * 消費クレジット量の表示用フォーマット (桁区切り + クレジット単位に丸める)。
 * 消費があるのに「0」と見せないよう、0.5 未満は「約0」と表示する。
 */
export function formatCredits(credits: number): string {
	if (credits > 0 && credits < 0.5) return "約0";
	return new Intl.NumberFormat("ja-JP", { maximumFractionDigits: 0 }).format(credits);
}

/**
 * 単価の表示用フォーマット。単価は 1 クレジットに満たない端数そのものが契約上の意味を
 * 持つため、消費量 (formatCredits) と違って丸めずに小数 2 桁まで見せる。
 */
export function formatRateCredits(credits: number): string {
	return new Intl.NumberFormat("ja-JP", { maximumFractionDigits: 2 }).format(credits);
}

const jstMonthDayFormatter = new Intl.DateTimeFormat("ja-JP", {
	timeZone: "Asia/Tokyo",
	month: "numeric",
	day: "numeric",
});

const jstDateFormatter = new Intl.DateTimeFormat("ja-JP", {
	timeZone: "Asia/Tokyo",
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
});

/** ライセンスの更新日は日本時間で数えるので、閲覧者のタイムゾーンによらず日本時間の「M/D」で見せる。 */
export function formatJstMonthDay(iso: string): string {
	return jstMonthDayFormatter.format(new Date(iso));
}

/** 日本時間の「YYYY/MM/DD」。 */
export function formatJstDate(iso: string): string {
	return jstDateFormatter.format(new Date(iso));
}

/** デバイスのライセンスの状態。終了・解約予約・支払い待ちが重なれば、この順に優先して 1 つにする。 */
export type DeviceLicenseStatus = "ended" | "cancelScheduled" | "unpaid" | "active";

export function deviceLicenseStatus(license: DeviceLicense): DeviceLicenseStatus {
	if (license.endedAt !== null) return "ended";
	if (license.cancelRequestedAt !== null) return "cancelScheduled";
	if (license.unpaidSince !== null) return "unpaid";
	return "active";
}

export const DEVICE_LICENSE_EVENT_LABELS: Readonly<Record<DeviceLicenseEventKind, string>> = {
	granted: "付与",
	cancelRequested: "解約予定",
	cancelWithdrawn: "解約の取り消し",
	ended: "解約",
	unpaid: "支払い待ち",
	paid: "支払い済み",
};

/** 更新の処理が操作者なしで起こす変化。 */
const AUTOMATIC_DEVICE_LICENSE_EVENT_KINDS: ReadonlySet<DeviceLicenseEventKind> = new Set([
	"ended",
	"unpaid",
	"paid",
]);

/** ライセンスの変化の操作者の表示。操作者が無ければ、更新の処理による変化は「自動」、それ以外は「記録なし」。 */
export function deviceLicenseEventActor(
	event: Pick<DeviceLicenseEvent, "kind" | "actorName">,
): string {
	if (event.actorName !== null) return event.actorName;
	return AUTOMATIC_DEVICE_LICENSE_EVENT_KINDS.has(event.kind) ? "自動" : "記録なし";
}

/** プロジェクトのクレジット上限の到達状況。 */
export type ProjectCreditLimitStatus = "none" | "softReached" | "hardReached";

/** 1 つの集計期間の消費と上限から到達状況を決める。 */
export function projectCreditPeriodLimitStatus(limit: {
	softLimitCredits: number | null;
	hardLimitCredits: number | null;
	usedCredits: number;
}): ProjectCreditLimitStatus {
	if (limit.hardLimitCredits !== null && limit.usedCredits >= limit.hardLimitCredits) {
		return "hardReached";
	}
	if (limit.softLimitCredits !== null && limit.usedCredits >= limit.softLimitCredits) {
		return "softReached";
	}
	return "none";
}

/**
 * プロジェクトの到達状況。日・月のうち重い方を取る。サーバーの停止判定は定期スイープで反映されるため、
 * 停止中の印が立っているか、どちらかの消費がハード・リミットに達していれば停止中とみなす。
 */
export function projectCreditLimitStatus(limit: {
	daily: Parameters<typeof projectCreditPeriodLimitStatus>[0];
	monthly: Parameters<typeof projectCreditPeriodLimitStatus>[0];
	hardLimitReached: boolean;
}): ProjectCreditLimitStatus {
	if (limit.hardLimitReached) return "hardReached";
	const statuses = [limit.daily, limit.monthly].map(projectCreditPeriodLimitStatus);
	if (statuses.includes("hardReached")) return "hardReached";
	if (statuses.includes("softReached")) return "softReached";
	return "none";
}

/**
 * 保存すると直ちに停止する集計期間。ハード・リミットを新しく設定・変更し、その値が今期の消費以下のもの。
 * 値を変えていない期間は、すでに停止中でも改めて確かめない。
 */
export function periodsStoppedBySave<P extends string>(
	current: NoInfer<Record<P, { hardLimitCredits: number | null; usedCredits: number }>>,
	nextHard: Record<P, number | null>,
): P[] {
	return (Object.keys(nextHard) as P[]).filter((period) => {
		const hard = nextHard[period];
		return (
			hard !== null &&
			hard !== current[period].hardLimitCredits &&
			hard <= current[period].usedCredits
		);
	});
}

/**
 * 上限の入力値を解釈する。空欄は設定なし (null)。保存先 (numeric(20, 8)) にそのまま入らない値
 * (0 以下・整数部 13 桁以上・小数 9 桁以上) はエラー文言を返す。小数を丸めて 0 になると消費 0 で上限に達するため。
 */
export function parseCreditLimitInput(
	input: string,
): { value: number | null; error: null } | { value: null; error: string } {
	const trimmed = input.trim().replaceAll(",", "");
	if (trimmed === "") return { value: null, error: null };
	const value = Number(trimmed);
	if (!/^\d{1,12}(\.\d{1,8})?$/.test(trimmed) || value <= 0) {
		return {
			value: null,
			error: "0 より大きく 1 兆未満、小数 8 桁以内の数値を入力してください。空欄なら設定しません。",
		};
	}
	return { value, error: null };
}
